import { createStorefrontApiClient } from "@shopify/storefront-api-client";
import {
  ObjectType,
  ComputingObject,
  Objects,
  getObject,
  getObjectByShopifyId,
} from "./objects";

// Re-export for backwards compatibility
export type { ComputingObject };
export { ObjectType, Objects, getObject, getObjectByShopifyId };

// Initialize the Storefront API client
const storefrontClient = createStorefrontApiClient({
  storeDomain: "checkout.spencer.place",
  apiVersion: "2025-04",
  publicAccessToken: "670faf48258417273072b727c02b3b61",
});

const PRODUCT_QUERY = `#graphql
  query Product($id: ID!) {
    product(id: $id) {
      id
      title
      description
      descriptionHtml
      media(first: 10) {
        edges {
          node {
            mediaContentType
            alt
            ... on Video {
              id
              sources {
                url
                mimeType
                format
                height
                width
              }
              previewImage {
                url
                altText
                width
                height
              }
            }
            ... on ExternalVideo {
              id
              embedUrl
              host
              previewImage {
                url
                altText
                width
                height
              }
            }
            ... on MediaImage {
              id
              image {
                url
                altText
                width
                height
              }
            }
            ... on Model3d {
              id
              sources {
                url
                mimeType
                format
              }
              previewImage {
                url
                altText
                width
                height
              }
            }
          }
        }
      }
      variants(first: 250) {
        edges {
          node {
            id
            title
            availableForSale
            currentlyNotInStock
            quantityAvailable
            price {
              amount
              currencyCode
            }
            compareAtPrice {
              amount
              currencyCode
            }
            sku
            selectedOptions {
              name
              value
            }
            image {
              url
              altText
            }
          }
        }
      }
      options {
        id
        name
        values
      }
    }
  }
`;

interface GraphQLVariantNode {
  id: string;
  title: string;
  availableForSale: boolean;
  currentlyNotInStock: boolean;
  quantityAvailable: number | null;
  price: {
    amount: string;
    currencyCode: string;
  };
  compareAtPrice?: {
    amount: string;
    currencyCode: string;
  };
  sku: string;
  selectedOptions: Array<{
    name: string;
    value: string;
  }>;
  image?: {
    url: string;
    altText?: string;
  };
}

interface GraphQLMediaNode {
  id: string;
  mediaContentType: MediaContentType;
  alt?: string;
  previewImage?: {
    url: string;
    altText?: string;
    width?: number;
    height?: number;
  };
}

interface GraphQLVideoNode extends GraphQLMediaNode {
  sources: VideoSource[];
}

interface GraphQLExternalVideoNode extends GraphQLMediaNode {
  embedUrl: string;
  host: string;
}

interface GraphQLMediaImageNode extends GraphQLMediaNode {
  image: {
    url: string;
    altText?: string;
    width?: number;
    height?: number;
  };
}

interface GraphQLModel3dNode extends GraphQLMediaNode {
  sources: Model3dSource[];
}

interface GraphQLProductResponse {
  product: {
    id: string;
    title: string;
    description: string;
    descriptionHtml: string;
    media: {
      edges: Array<{
        node:
          | GraphQLVideoNode
          | GraphQLExternalVideoNode
          | GraphQLMediaImageNode
          | GraphQLModel3dNode;
      }>;
    };
    variants: {
      edges: Array<{
        node: GraphQLVariantNode;
      }>;
    };
    options: Array<{
      id: string;
      name: string;
      values: string[];
    }>;
  };
}

export interface VariantDetail {
  id: string;
  title: string;
  isAvailable: boolean;
  currentlyNotInStock: boolean;
  quantityAvailable: number | null;
  price: string;
  compareAtPrice?: string;
  sku: string;
  selectedOptions: Array<{
    name: string;
    value: string;
  }>;
  image?: {
    url: string;
    altText?: string;
  };
}

export interface ProductOption {
  id: string;
  name: string;
  values: string[];
}

export interface VideoSource {
  url: string;
  mimeType: string;
  format: string;
  height?: number;
  width?: number;
}

export interface Model3dSource {
  url: string;
  mimeType: string;
  format: string;
}

export interface ImageDetails {
  url: string;
  altText?: string;
  width?: number;
  height?: number;
}

export interface ProductMedia {
  id?: string;
  url: string;
  altText?: string;
  width?: number;
  height?: number;
  mediaContentType: MediaContentType;
  sources?: VideoSource[] | Model3dSource[];
  embedUrl?: string;
  host?: string;
  previewImageUrl?: string;
}

export enum MediaContentType {
  Image = "IMAGE",
  Video = "VIDEO",
  ExternalVideo = "EXTERNAL_VIDEO",
  Model3D = "MODEL_3D",
}

export interface ProductDetail {
  id: string;
  title: string;
  description: string;
  descriptionHtml: string;
  media: ProductMedia[];
  variants: VariantDetail[];
  options: ProductOption[];
}

/**
 * Fetches basic product information (title and image) from Shopify
 * @param productId The Shopify product ID
 * @returns Promise with the product title and image URL
 */
export const getProductInfo = async (
  productId: string
): Promise<ProductDetail> => {
  try {
    const formattedId = `gid://shopify/Product/${productId}`;

    const response = await storefrontClient.request(PRODUCT_QUERY, {
      variables: { id: formattedId },
    });

    const data = response.data as GraphQLProductResponse;

    if (!data.product) {
      throw new Error(`Product with ID ${productId} not found`);
    }

    // Transform the GraphQL response into our expected format
    const variantsInfo = data.product.variants.edges.map(
      ({ node: variant }: { node: GraphQLVariantNode }) => ({
        id: variant.id,
        title: variant.title,
        isAvailable: variant.availableForSale,
        currentlyNotInStock: variant.currentlyNotInStock,
        quantityAvailable: variant.quantityAvailable,
        price: variant.price.amount,
        compareAtPrice: variant.compareAtPrice?.amount,
        sku: variant.sku,
        selectedOptions: variant.selectedOptions,
        image: variant.image,
      })
    );

    const imagesInfo = data.product.media.edges.map(({ node: media }) => {
      // Type assertions for different media types
      const imageNode = media as GraphQLMediaImageNode;
      const videoNode = media as GraphQLVideoNode;
      const externalVideoNode = media as GraphQLExternalVideoNode;
      const model3dNode = media as GraphQLModel3dNode;

      // Base media object with required fields
      const baseMedia: ProductMedia = {
        mediaContentType: media.mediaContentType,
        altText: media.alt,
        id: media.id,
        url: "", // Will be overwritten in each case
      };

      switch (media.mediaContentType) {
        case MediaContentType.Image:
          return {
            ...baseMedia,
            url: imageNode.image.url,
            width: imageNode.image.width,
            height: imageNode.image.height,
            altText: imageNode.image.altText || imageNode.alt,
          };
        case MediaContentType.Video:
          return {
            ...baseMedia,
            url: videoNode.sources[0]?.url || "",
            sources: videoNode.sources,
            width: videoNode.sources[0]?.width,
            height: videoNode.sources[0]?.height,
            previewImageUrl: videoNode.previewImage?.url,
          };
        case MediaContentType.ExternalVideo:
          return {
            ...baseMedia,
            url: externalVideoNode.embedUrl,
            host: externalVideoNode.host,
            previewImageUrl: externalVideoNode.previewImage?.url,
          };
        case MediaContentType.Model3D:
          // TODO: Model3D assets are not currently handled in the frontend renderer
          return {
            ...baseMedia,
            url: model3dNode.previewImage?.url || "", // Fallback to preview image for now
            sources: model3dNode.sources,
          };
        default:
          console.warn(`Unhandled media type: ${media.mediaContentType}`);
          return {
            ...baseMedia,
            url: media.previewImage?.url || "",
          };
      }
    });

    return {
      id: data.product.id,
      title: data.product.title,
      description: data.product.description,
      descriptionHtml: data.product.descriptionHtml,
      media: imagesInfo,
      variants: variantsInfo,
      options: data.product.options,
    };
  } catch (error) {
    console.error(`Error fetching product ${productId}:`, error);
    throw error;
  }
};
